//Basic Item type
import { CartBundleOption, CartConfigOption, CartItemPrice, MediaEntry } from 'src/core/state/cart';

import { UrlRewriteEntityTypeEnum } from '../../../../__generated__/globalTypes';

interface Item {
    id: string;
    name: string;
    sku?: string;
    coupon?: string;
    discount?: string;
    index?: number;
    item_list_name?: string;
    item_list_id?: string;
    affiliation?: string;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_variant?: string;
    price?: number;
}

export type GtmPageView = {
    event: 'pageview';
};

//View Item pattern is used by many messages
type ViewItem = {
    products: Item[];
    actionField?: {
        list: string;
    };
};

type ViewItemDetail = {
    detail: {
        eventId: string;
        products: Item[];
        actionField?: {
            list: string;
        };
    };
};

export type GtmViewItem = {
    event: 'eec.detail';
    eecEventName: string;
    ecommerce: ViewItemDetail;
};

interface CartItem extends Item {
    quantity: string;
}

export type CartRawItem =
    | {
          __typename: 'ConfigurableCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
          configurable_options: CartConfigOption[];
      }
    | {
          __typename: 'BundleCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
          bundle_options: CartBundleOption[];
      }
    | {
          __typename: 'SimpleCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
      };

export type CartItemJH =
    | {
          __typename: 'ConfigurableCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
          configurable_options: CartConfigOption[];
          customised?: boolean;
      }
    | {
          __typename: 'BundleCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
          bundle_options: CartBundleOption[];
          customised?: boolean;
      }
    | {
          __typename: 'SimpleCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
          customised?: boolean;
      };

type BasketInfo = {
    checkout: {
        actionField?: {
            step: number;
        };
        products: Item[];
    };
};

export type GtmBasket = {
    event: 'eec.checkout';
    eecEventName: string;
    ecommerce: BasketInfo;
};

type AddPaymentInfo = {
    checkout: {
        actionField?: {
            step: number;
            option: string;
        };
        products: Item[];
    };
};

export type GtmAddPaymentInfo = {
    event: 'eec.checkout';
    eecEventName: string;
    ecommerce: AddPaymentInfo;
};

type AddShippingInfo = {
    checkout: {
        eventId?: string;
        actionField?: {
            step: number;
            option: string;
        };
        products: Item[];
    };
};

export type GtmAddShippingInfo = {
    event: 'eec.checkout';
    eecEventName: string;
    ecommerce: AddShippingInfo;
};

type AddToCart = {
    add: {
        eventId?: string;
        products: CartItem[];
        actionField?: {
            list: string;
        };
    };
};

type AddToCartWithVersion = {
    add: {
        products: Item[];
        actionField?: {
            list: string;
        };
    };
};

export type GtmAddtoCart = {
    event: 'eec.add';
    eecEventName: string;
    ecommerce: AddToCart;
};

export type GtmAddtoCartWithVersion = {
    event: string;
    eecEventName: string;
    ecommerce: AddToCartWithVersion;
    eventCallback: () => void;
};

export enum AddBundleVersion {
    V1 = 'eec.addBundleV1',
    V2 = 'eec.addBundleV2',
}

type AddToWishlist = ViewItem;

export type GtmAddToWishlist = {
    event: 'add_to_wishlist';
    ecommerce: AddToWishlist;
};

export type GtmLogin = {
    event: 'login';
    method: string;
};

type Purchase = {
    currencyCode?: string;
    store?: string;
    purchase: {
        eventId?: string;
        actionField?: {
            id?: string;
            revenue?: number;
            tax?: number;
            affiliation?: string;
            shipping?: number;
            coupon?: string;
        };
        products: Item[];
        customisedOrder: boolean;
        existingCustomer: boolean;
        newCustomer: boolean;
    };
};

export type GtmPurchase = {
    event: 'eec.purchase';
    eecEventName: string;
    ecommerce: Purchase;
};

type RemoveFromCart = {
    remove: {
        actionField?: {
            list: string;
        };
        products: Item[];
    };
};

export type GtmRemoveFromCart = {
    event: 'eec.remove';
    eecEventName: string;
    ecommerce: RemoveFromCart;
};

export type GtmSearch = {
    event: 'search';
    search_term: string;
};

type SelectItem = ViewItemList;

export type GtmSelectItem = {
    event: 'select_item';
    ecommerce: SelectItem;
};

interface PromotionItem extends Item {
    promotion_id?: string;
    promotion_name?: string;
    creative_slot?: string;
    location_id?: string;
}

type SelectPromotion = {
    products: PromotionItem[];
    promotion_id?: string;
    promotion_name?: string;
};

export type GtmSelectPromotion = {
    event: 'select_promotion';
    ecommerce: SelectItem;
};

type ViewCart = ViewItem;

export type GtmViewCart = {
    event: 'view_cart';
    ecommerce: ViewCart;
};

type ViewItemList = {
    impressions: (Item | undefined)[];
};

export type GtmViewItemList = {
    event: 'eec.impressions';
    eecEventName: string;
    ecommerce: ViewItemList;
};

type ViewPromotion = SelectPromotion;

export type GtmViewPromotion = {
    event: 'select_promotion';
    ecommerce: ViewPromotion;
};

type GtmCheckoutPurchase = {
    event: string;
    order_value: string;
    order_id: string;
    enhanced_conversion_data: {
        email: string;
        phone_number: string;
        first_name: string;
        last_name: string;
        street: string;
        city: string;
        region: string;
        postal_code: string;
        country: string;
    };
    isOrderCustomised?: boolean;
};

export type GtmEvents =
    | GtmBasket
    | GtmAddPaymentInfo
    | GtmAddShippingInfo
    | GtmAddtoCart
    | GtmAddToWishlist
    | GtmLogin
    | GtmPurchase
    | GtmRemoveFromCart
    | GtmSearch
    | GtmSelectItem
    | GtmSelectPromotion
    | GtmViewCart
    | GtmViewItem
    | GtmViewItemList
    | GtmViewPromotion
    | GtmPageView
    | GtmAddtoCartWithVersion
    | GtmCheckoutPurchase
    | { pageType: UrlRewriteEntityTypeEnum | undefined | null }
    | { event?: 'login' | 'register'; userId: string };

type CartProduct = {
    __typename: 'BundleProduct' | 'ConfigurableProduct' | 'SimpleProduct' | 'GroupedProduct' | 'VirtualProduct';
    small_image: MediaEntry;
    name: string;
    sku: string;
    pathname: string;
    buy_price?: number;
};
